import { inject, Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { Action, Selector, State, StateContext } from '@ngxs/store'
import { Abgabeweg } from '../model/selfservice.model'
import { ResetState } from './global.actions'
import { ClearProduktCart } from './produkt-cart.state'

export interface AppStateModel {
  sanitaetshausId?: string
  partnerId?: string
  patientId?: string
  yearOfBirth?: string

  embedded?: boolean

  /**
   * Wie die Box ankommen soll — Versand oder Filialabholung. Kommt als `aw` in der URL, wenn der
   * Leistungserbringer den Weg im Rahmen um das Portal schon gesetzt hat. An ihm haengt das
   * Sortiment; fehlt er, nimmt das Backend den Weg, den das Haus selbst fuehrt.
   */
  abgabeweg?: Abgabeweg

  /**
   * Wurde der Abgabeweg BEWUSST gewaehlt (Kartenseite) — im Unterschied zu „kam als `aw` aus der
   * URL"? Nur das beendet die Rueckfrage.
   *
   * <p>Der Unterschied traegt: der Leistungserbringer-Rahmen haengt `aw` an JEDEN Link. Wuerde schon
   * das als Entscheidung gelten, kaeme die Frage nie — und die Patient:in bekaeme das Sortiment
   * zugeteilt, statt es zu waehlen.
   *
   * <p>Im **eingebetteten** Rahmen laesst der `abgabewegAuswahlGuard` ein vorgegebenes `aw`
   * trotzdem durch: dort waehlt die Button-Group ueber dem iframe. Das laeuft ueber die URL, nicht
   * ueber dieses Feld — es bleibt die Antwort auf „hat ein Mensch auf die Karte geklickt?".
   */
  abgabewegBestaetigt?: boolean

  /**
   * Audit ID for tracing requests across services
   */
  auditId?: string
}

export class SetAppState {
  static readonly type = '[App] Set SanitaetshausId, PartnerId, PatientId and YearOfBirth'
  constructor(
    public sanitaetshausId?: string,
    public partnerId?: string,
    public patientId?: string,
    public yearOfBirth?: string,
  ) {}
}

export class SetYearOfBirth {
  static readonly type = '[App] Set Year of Birth'
  constructor(public yearOfBirth?: string) {}
}

export class SetAbgabeweg {
  static readonly type = '[App] Set Abgabeweg'
  /**
   * @param bestaetigt `true` nur, wenn ein Mensch auf die Karte geklickt hat. Die Uebernahme aus der
   *   URL (`aw`) ist eine Vorbelegung, keine Entscheidung.
   * @param ausEingebettetemRahmen `true`, wenn das `aw` aus dem iframe der Leistungserbringer-App
   *   kommt (`embedded=true`). Dort IST es die Entscheidung — die Button-Group ueber dem Fenster
   *   laedt es bei jedem Wechsel mit neuem `aw` neu, die Kartenseite laeuft nie.
   */
  constructor(
    public abgabeweg?: Abgabeweg,
    public bestaetigt = false,
    public ausEingebettetemRahmen = false,
  ) {}
}

export class SetEmbedded {
  static readonly type = '[App] Set Embedded'
  constructor(public embedded?: boolean) {}
}

export class SetAuditId {
  static readonly type = '[App] Set Audit ID'
  constructor(public auditId?: string) {}
}

@Injectable()
@State<AppStateModel>({
  name: 'app',
  defaults: {},
})
export class AppState {
  private readonly router = inject(Router)

  @Selector()
  static getSanitaetshausId(state: AppStateModel) {
    return state.sanitaetshausId
  }
  @Selector()
  static getParnterId(state: AppStateModel) {
    return state.partnerId
  }
  @Selector()
  static isEmbedded(state: AppStateModel) {
    return !!state.embedded
  }
  @Selector()
  static getState(state: AppStateModel) {
    return state
  }

  @Action(SetAppState)
  setSanitaetshausId(ctx: StateContext<AppStateModel>, action: SetAppState) {
    const { sanitaetshausId, partnerId, patientId } = ctx.getState()
    if (sanitaetshausId !== action.sanitaetshausId || partnerId !== action.partnerId || patientId !== action.patientId) {
      console.log('Different Session detected, resetting state')
      ctx.dispatch(new ResetState())
    }
    ctx.patchState({
      sanitaetshausId: action.sanitaetshausId,
      partnerId: action.partnerId,
      patientId: action.patientId,
      yearOfBirth: action.yearOfBirth,
    })
  }

  @Action(SetYearOfBirth)
  setYearOfBirth(ctx: StateContext<AppStateModel>, action: SetYearOfBirth) {
    ctx.dispatch(new SetAppState(ctx.getState().sanitaetshausId, ctx.getState().partnerId, ctx.getState().patientId, action.yearOfBirth))
  }

  /**
   * Ein Wechsel des Wegs wechselt das SORTIMENT. Positionen aus dem alten stuenden sonst weiter im
   * Korb, ohne bestellbar zu sein — sichtbar wuerde das erst beim Absenden.
   *
   * <p>Es zaehlt nur ein Aufruf, der den Weg wirklich ENTSCHEIDET: der Klick auf die Kartenseite
   * oder der eingebettete Leistungserbringer-Rahmen. Das blosse Nachziehen eines `aw` aus der URL
   * darf keinen gefuellten Korb leeren — der Leistungserbringer haengt es an JEDEN Link, den die
   * Patient:in bekommt, und die Query-Param-Auswertung laeuft bei jeder Navigation erneut.
   */
  @Action(SetAbgabeweg)
  setAbgabeweg(ctx: StateContext<AppStateModel>, action: SetAbgabeweg) {
    const { abgabeweg: bisher, abgabewegBestaetigt } = ctx.getState()
    const entscheidung = action.bestaetigt || action.ausEingebettetemRahmen

    if (entscheidung && bisher && action.abgabeweg && bisher !== action.abgabeweg) {
      ctx.dispatch(new ClearProduktCart())
    }

    ctx.patchState({
      // Eine Vorbelegung ueberschreibt eine bereits getroffene Wahl NICHT — weder den Wert noch das
      // Kennzeichen. Bis 2026-08-25 stand hier `abgabeweg: action.abgabeweg`, und damit fiel die Wahl
      // beim naechsten Seitenaufruf auf das `aw` der URL zurueck: der Leistungserbringer haengt es an
      // JEDEN Link, ein Reload dispatcht es erneut, und der Korb wurde dabei nicht geleert
      // (`bestaetigt` ist false). `produkt.service.ts` schickte danach den falschen Weg an Produktliste
      // UND Anlage, waehrend der Korb noch die Positionen des gewaehlten Sortiments trug.
      abgabeweg: entscheidung || !abgabewegBestaetigt ? action.abgabeweg : bisher,
      abgabewegBestaetigt: action.bestaetigt || abgabewegBestaetigt,
    })
  }

  /**
   * Beim Sitzungswechsel faellt die getroffene Wahl weg.
   *
   * <p>`AppState` wird persistiert (`withNgxsStoragePlugin`) und hatte bis 08/2026 KEINEN
   * `ResetState`-Handler. Ein einmal bestaetigter Abgabeweg ueberlebte damit den Wechsel auf einen
   * anderen Patienten oder ein anderes Haus — der Guard brach dann in seiner ersten Zeile ab und die
   * Frage kam nie wieder. Identitaet und Audit-Id bleiben stehen: die setzt `SetAppState` unmittelbar
   * danach ohnehin neu.
   */
  @Action(ResetState)
  resetAuswahl(ctx: StateContext<AppStateModel>) {
    ctx.patchState({ abgabeweg: undefined, abgabewegBestaetigt: undefined })
  }

  @Action(SetEmbedded)
  setEmbedded(ctx: StateContext<AppStateModel>, action: SetEmbedded) {
    ctx.patchState({
      embedded: action.embedded,
    })
  }

  @Action(SetAuditId)
  setAuditId(ctx: StateContext<AppStateModel>, action: SetAuditId) {
    ctx.patchState({
      auditId: action.auditId,
    })
  }
}
